import * as E from 'fp-ts/lib/Either'
import { sequenceT } from 'fp-ts/lib/Apply'
import { pipe } from 'fp-ts/lib/function'
import * as TE from 'fp-ts/lib/TaskEither'
import {} from 'googlemaps'

type LatLngBounds = google.maps.LatLngBounds

interface Position {
    lat: number;
    lng: number;
}

const extendBounds = (acc: LatLngBounds, p: Position): LatLngBounds => acc.extend(p)

const hasCorrectFormat = (p: Position): boolean => {
  const pattern = new RegExp('^-?([1-8]?[1-9]|[1-9]0)\\.{1}\\d{1,6}')
  return [String(p.lat), String(p.lng)].reduce((b: boolean, s: string) => b && pattern.test(s), true)
}

const mkLatLngBounds = (bounds: LatLngBounds) => {
  return function (px: Position[]): LatLngBounds {
    return [...px]
      .filter(hasCorrectFormat)
      .reduce(extendBounds, bounds)
  }
}

export const fitBounds = (google: any, mapRef: any) => (px: Position[]): Promise<E.Either<void, void>> => {
  const positions: E.Either<Error, Position[]> = px.length === 0 ? E.left(new Error('Nothing to bound.')) : E.right(px)
  const getBoundsFn: E.Either<Error, ((px: Position[]) => LatLngBounds)> = pipe(
    new google.maps.LatLngBounds(),
    E.fromNullable(new Error('Could not initiate bounding function.')),
    E.map(mkLatLngBounds)
  )

  const getBounds: TE.TaskEither<Error, LatLngBounds> = pipe(
    sequenceT(E.either)(getBoundsFn, positions),
    E.map(([fn, px]) => fn(px)),
    TE.fromEither
  )

  const getMap: TE.TaskEither<Error, google.maps.Map> = TE.tryCatch(
    () => mapRef.$mapPromise,
    e => new Error(String(e))
  )

  return pipe(
    sequenceT(TE.taskEither)(getBounds, getMap),
    TE.map(([bs, map]) => map.fitBounds(bs)),
    TE.mapLeft(console.error)
  )()
}
